import { computed, ref } from "vue";
import vuetify from "@/core/plugins/vuetify";
import { themeColors } from "@/core/config/themeDefinitions";
import { getExpectedOrigin } from "@/components/IFrame/iframehelper";

const VALID_THEMES = [
  "light",
  "dark",
  "light-colorblind",
  "dark-colorblind",
  "system",
  "system-colorblind"
];

const currentTheme = ref("light");

const isDark = computed(
  () =>
    currentTheme.value === "dark" || currentTheme.value === "dark-colorblind"
);

function applyTheme(themeMode) {
  if (!VALID_THEMES.includes(themeMode)) return;

  let tempThemeMode = themeMode;

  if (tempThemeMode.includes("system")) {
    const isColorblind = tempThemeMode.includes("colorblind");
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    tempThemeMode = isColorblind ? `${systemTheme}-colorblind` : systemTheme;
  }

  currentTheme.value = tempThemeMode;

  // Set data-theme attribute on <html> for CSS custom property switching
  document.documentElement.setAttribute("data-theme", tempThemeMode);

  // Determine Vuetify theme key (light or dark)
  const vuetifyThemeKey = isDark.value ? "dark" : "light";

  // Update Vuetify theme colors for the active variant (handles colorblind)
  const colors = themeColors[tempThemeMode];
  if (colors) {
    Object.assign(vuetify.framework.theme.themes[vuetifyThemeKey], colors);
  }

  // Toggle Vuetify dark mode
  vuetify.framework.theme.dark = isDark.value;
}

function isValidThemeMessage(data) {
  if (!data || typeof data !== "object") return false;
  if (data.type !== "theme") return false;
  if (!data.themeMode || typeof data.themeMode !== "string") return false;
  return VALID_THEMES.includes(data.themeMode);
}

let listenerAttached = false;

function initThemeListener() {
  if (listenerAttached) return;
  listenerAttached = true;

  window.addEventListener("message", event => {
    const expectedOrigin = getExpectedOrigin();
    if (!expectedOrigin || event.origin !== expectedOrigin) return;

    if (isValidThemeMessage(event.data)) {
      applyTheme(event.data.themeMode);
    }
  });
}

export function useTheme() {
  return {
    currentTheme,
    isDark,
    applyTheme,
    initThemeListener
  };
}
